import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../config/supabaseClient';
import toast from 'react-hot-toast';

const ResetPasswordConfirm: React.FC = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showResetForm, setShowResetForm] = useState(false);
  const navigate = useNavigate();
  
  // Add Supabase auth listener for PASSWORD_RECOVERY event
  useEffect(() => {
    // This will detect if the user arrived via a password reset link
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('Auth event detected:', event);
      if (event === 'PASSWORD_RECOVERY') {
        // User came from a password reset link
        console.log('Password recovery event detected');
        setShowResetForm(true);
        toast.success('Please enter your new password');
      }
    });
    
    // If the form isn't triggered by the event, check URL for reset token
    // This is a fallback for our existing flow
    const checkResetToken = async () => {
      try {
        // Get URL hash parameters or query parameters that might indicate reset token
        const urlParams = new URLSearchParams(window.location.search);
        const hashParams = new URLSearchParams(window.location.hash.replace('#', ''));
        
        const hasResetToken = 
          urlParams.has('type') && urlParams.get('type') === 'recovery' || 
          hashParams.has('type') && hashParams.get('type') === 'recovery' ||
          hashParams.has('access_token'); // Direct hash with access token (common in password reset links)
          
        if (hasResetToken) {
          console.log('Reset token found in URL');
          
          // If we have an access_token in the hash, try to set the session
          if (hashParams.has('access_token') && hashParams.has('refresh_token')) {
            try {
              const { data, error } = await supabase.auth.setSession({
                access_token: hashParams.get('access_token') || '',
                refresh_token: hashParams.get('refresh_token') || ''
              });
              
              if (error) {
                console.error('Error setting session from hash params:', error);
              } else {
                console.log('Successfully set session from hash params');
              }
            } catch (err) {
              console.error('Error processing hash params:', err);
            }
          }
          
          setShowResetForm(true);
        } else {
          // If we're on this page but no auth event and no token, show the form anyway
          // This allows direct navigation to the page for users who are already authenticated
          setShowResetForm(true);
        }
      } catch (err) {
        console.error('Error checking reset token:', err);
        setShowResetForm(true); // Show form on error as fallback
      }
    };
    
    checkResetToken();
    
    return () => {
      authListener?.subscription?.unsubscribe();
    };
  }, []);
  

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    // Validate password strength
    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Update the password
      const { error } = await supabase.auth.updateUser({
        password: password
      });
      
      if (error) {
        throw error;
      }
      
      toast.success('Password has been successfully reset!');

      try {
        const redirect = localStorage.getItem('post_reset_redirect');
        if (redirect && redirect.trim().length > 0) {
          localStorage.removeItem('post_reset_redirect');
          navigate(redirect);
          return;
        }
      } catch {
        // ignore storage errors
      }

      navigate('/');
    } catch (err) {
      console.error('Password update error:', err);
      setError(err instanceof Error ? err.message : 'Failed to reset password');
      toast.error('Error resetting password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-full flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Create new password
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Please enter your new password below
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {!showResetForm ? (
            <div className="text-center py-6">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p>Verifying your reset link...</p>
            </div>
          ) : (
            <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="rounded-md bg-red-50 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">{error}</h3>
                  </div>
                </div>
              </div>
            )}

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                New Password
              </label>
              <div className="mt-1">
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>
            </div>

            <div>
              <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700">
                Confirm New Password
              </label>
              <div className="mt-1">
                <input
                  id="confirm-password"
                  name="confirm-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>
            </div>

            <div>
              <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-400 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Resetting Password...
                </>
              ) : (
                'Reset Password'
              )}
            </button>
            </div>
          </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordConfirm;
